import { useRef, useLayoutEffect, useEffect, useCallback } from 'react';

// Soepel bladeren tussen maanden in een kalender: de kaart volgt je vinger, en bij
// loslaten glijdt de vorige/volgende maand er aaneensluitend in. Gebruikt door de
// kalender van de schoonmaker en van de verhuurder.
//
// De kalender zelf rendert 3 "panelen" in een "track": vorige maand (links buiten
// beeld), huidige maand, volgende maand (rechts buiten beeld). Dit hook verschuift
// enkel de track (translate3d, dus op de GPU, zonder React-herrender tijdens het
// vegen). Pas als de animatie klaar is, wordt `onNext`/`onPrev` aangeroepen; zodra
// de nieuwe maand (`monthKey`) gerenderd is, springt de track onzichtbaar terug op 0.
//
// Gebruik:
//   const swipe = useMonthSwipe({ monthKey, onNext, onPrev });
//   <div ref={swipe.cardRef} {...swipe.handlers} className="card (overflow:hidden; touch-action:pan-y)">
//     <div ref={swipe.trackRef} className="track (position:relative; will-change:transform)"> ...3 panelen... </div>
//   </div>
//   swipe.slide(1) / swipe.slide(-1) voor pijltjesknoppen.
const MIN_DURATION = 180;
const MAX_DURATION = 320;
// Remt snel af maar komt DECISIEF aan: het laatste stukje (de laatste ~3px) duurt zo maar
// ~25ms i.p.v. ~55ms. Met de vroegere curve (eindsnelheid 0, "0.22, 0.8, 0.32, 1") kroop
// de kalender de laatste paar pixels nog traag naar zijn plek - op een telefoon zag je dat
// als een klein nasukkelend bewegingetje aan het einde van het bladeren.
const EASING = 'cubic-bezier(0.25, 0.8, 0.4, 0.96)';

export default function useMonthSwipe({ monthKey, onNext, onPrev }) {
  const cardNode = useRef(null);
  const trackRef = useRef(null);
  const callbacks = useRef({ onNext, onPrev });
  callbacks.current = { onNext, onPrev };
  const st = useRef({
    touch: null, // { x, y, axis, dx, lastDx, lastT, velocity }
    animating: false,
    pendingDir: 0,
    raf: 0,
    pendingX: 0,
    fallback: 0,
    wheelLock: 0,
  });

  // Breedte van 1 paneel = breedte van de track (de buurpanelen staan op exact
  // -100% / +100% daarvan). Bewust NIET de breedte van de kaart: die telt de rand
  // mee (+2px) en wordt op hele pixels afgerond, waardoor de nieuwe maand aan het
  // einde van de animatie nog even naar links/rechts "verspringt" bij het terugzetten.
  // getBoundingClientRect geeft de exacte (kommagetal-)breedte, ongeacht de translatie.
  const cardWidth = () => {
    const el = trackRef.current || cardNode.current;
    return el ? el.getBoundingClientRect().width : 300;
  };

  const applyX = (x, durationMs) => {
    const el = trackRef.current;
    if (!el) return;
    el.style.transition = durationMs ? `transform ${durationMs}ms ${EASING}` : 'none';
    el.style.transform = `translate3d(${x}px, 0, 0)`;
  };

  // Tijdens het vegen max. 1 stijlupdate per beeld (rAF) i.p.v. bij elk touch-event.
  const dragTo = (x) => {
    const s = st.current;
    s.pendingX = x;
    if (s.raf) return;
    s.raf = window.requestAnimationFrame(() => {
      s.raf = 0;
      applyX(s.pendingX, 0);
    });
  };
  const cancelDrag = () => {
    const s = st.current;
    if (s.raf) {
      window.cancelAnimationFrame(s.raf);
      s.raf = 0;
    }
  };

  // Schuift naar de volgende (dir = 1) of vorige (dir = -1) maand en meldt dat daarna.
  const commit = (dir, fromX = 0, velocity = 0) => {
    const s = st.current;
    const el = trackRef.current;
    if (!el) return;
    cancelDrag();
    s.animating = true;
    s.pendingDir = dir;
    const target = -dir * cardWidth();
    const remaining = Math.abs(target - fromX);
    const speed = Math.max(Math.abs(velocity), 0.9); // px per ms
    const duration = Math.round(Math.min(MAX_DURATION, Math.max(MIN_DURATION, remaining / speed)));
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      el.removeEventListener('transitionend', onEnd);
      const { onNext: next, onPrev: prev } = callbacks.current;
      (dir > 0 ? next : prev)?.();
      // Als de ouder de maand om een of andere reden niet wijzigt, niet blijven hangen.
      window.clearTimeout(s.fallback);
      s.fallback = window.setTimeout(() => {
        if (s.pendingDir !== 0) {
          s.pendingDir = 0;
          s.animating = false;
          applyX(0, MIN_DURATION);
        }
      }, 600);
    };
    const onEnd = (e) => {
      if (e.target === el && e.propertyName === 'transform') finish();
    };
    el.addEventListener('transitionend', onEnd);
    // Forceer een reflow zodat de transitie vanaf de huidige positie start.
    void el.offsetWidth; // eslint-disable-line no-void
    applyX(target, duration);
    window.setTimeout(finish, duration + 80); // vangnet als transitionend niet afvuurt
  };

  // Nieuwe maand is gerenderd: de track onzichtbaar (zonder animatie) terug op 0 zetten,
  // vóór het scherm hertekend wordt.
  useLayoutEffect(() => {
    const s = st.current;
    if (s.pendingDir !== 0) {
      s.pendingDir = 0;
      window.clearTimeout(s.fallback);
      applyX(0, 0);
      s.animating = false;
    }
  }, [monthKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const slide = (dir) => {
    if (st.current.animating) return;
    commit(dir, 0, 0);
  };

  const onTouchStart = (e) => {
    const s = st.current;
    if (s.animating || e.touches.length !== 1) { s.touch = null; return; }
    const t = e.touches[0];
    s.touch = { x: t.clientX, y: t.clientY, axis: null, dx: 0, lastDx: 0, lastT: performance.now(), velocity: 0 };
  };

  const onTouchMove = (e) => {
    const s = st.current;
    const tc = s.touch;
    if (!tc || e.touches.length !== 1) return;
    const t = e.touches[0];
    const dx = t.clientX - tc.x;
    const dy = t.clientY - tc.y;
    if (!tc.axis) {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
      tc.axis = Math.abs(dx) > Math.abs(dy) * 1.2 ? 'x' : 'y';
    }
    if (tc.axis !== 'x') return;
    const now = performance.now();
    const dt = now - tc.lastT;
    if (dt > 12) {
      tc.velocity = (dx - tc.lastDx) / dt; // px/ms (negatief = naar links)
      tc.lastDx = dx;
      tc.lastT = now;
    }
    tc.dx = dx;
    dragTo(dx);
  };

  const onTouchEnd = () => {
    const s = st.current;
    const tc = s.touch;
    s.touch = null;
    if (!tc || tc.axis !== 'x') return;
    const width = cardWidth();
    const { dx, velocity } = tc;
    const flick = Math.abs(velocity) > 0.4 && Math.abs(dx) > 24;
    if (Math.abs(dx) > width * 0.22 || flick) {
      commit(dx < 0 ? 1 : -1, dx, velocity);
    } else {
      cancelDrag();
      s.animating = true;
      applyX(0, 220);
      window.setTimeout(() => { s.animating = false; }, 230);
    }
  };

  // Horizontaal scrollen met trackpad/muis: 1 maand per beweging.
  const onWheel = (e) => {
    if (Math.abs(e.deltaX) < 40 || Math.abs(e.deltaX) < Math.abs(e.deltaY)) return;
    const s = st.current;
    const now = Date.now();
    if (s.animating || now - s.wheelLock < 600) return;
    s.wheelLock = now;
    commit(e.deltaX > 0 ? 1 : -1, 0, 0);
  };

  // Zodra een veeg horizontaal is, mag de pagina niet meer verticaal meebewegen: dat vraagt
  // een niet-passieve touchmove-listener. Callback-ref, want de kaart kan later in beeld komen.
  const blockVertical = useRef((e) => {
    if (st.current.touch?.axis === 'x' && e.cancelable) e.preventDefault();
  });
  const cardRef = useCallback((node) => {
    const handler = blockVertical.current;
    if (cardNode.current) cardNode.current.removeEventListener('touchmove', handler);
    cardNode.current = node;
    if (node) node.addEventListener('touchmove', handler, { passive: false });
  }, []);

  useEffect(() => () => {
    const s = st.current;
    window.clearTimeout(s.fallback);
    if (s.raf) window.cancelAnimationFrame(s.raf);
  }, []);

  return {
    cardRef,
    trackRef,
    slide,
    handlers: {
      onTouchStart,
      onTouchMove,
      onTouchEnd,
      onTouchCancel: onTouchEnd,
      onWheel,
    },
  };
}
